import React, { useState, useEffect, useCallback } from 'react'
import {
  FormattedMessage,
  injectIntl,
} from 'react-intl'
import CountryStateCombo from './CountryStateDropdowns'
import NavigationBar from '../../partials/NavigationBar'
import Footer from '../../partials/Footer'
import countries from './countriesList'

import {
  DivAccount,
  AuthCard,
  Subtitle,
  Asterisk,
  Title,
  MessageError,
  TitleError,
  Message,
  StyledInput,
  Label,
  ButtonWhite,
} from './styledComponents'

const Address = function Address (props) {
  const {
    intl,
    onPrevious,
    submitError,
    logout,
    onSubmit,
    values = {},
  } = props

  const [streetAddress, setStreetAddress] = useState('')
  const [city, setCity] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [countryIndex, setCountryIndex] = useState(-1)
  const [stateIndex, setStateIndex] = useState(-1)
  const [error, setError] = useState('')
  const [errorFields, setErrorFields] = useState([])

  const { formatMessage } = intl

  useEffect(() => {
    const { address } = values

    if (address) {
      setStreetAddress(address.address || '')
      setCity(address.city || '')
      setZipCode(address.zip_code || '')

      const newCountryIndex = countries.findIndex((element) => element.country === address.country)
      setCountryIndex(newCountryIndex)

      const newStateIndex = newCountryIndex > -1 && countries[newCountryIndex]
        ? countries[newCountryIndex].states.findIndex((element) => element === address.state)
        : -1
      setStateIndex(newStateIndex)
    } else {
      setStreetAddress('')
      setCity('')
      setZipCode('')
      setCountryIndex(-1)
      setStateIndex(-1)
    }
  }, [values])

  const handleAddressChange = useCallback((event) => {
    setStreetAddress(event.target.value)
    setError('')
  }, [])

  const handleCityChange = useCallback((event) => {
    setCity(event.target.value)
    setError('')
  }, [])

  const handleZipCodeChange = useCallback((event) => {
    setZipCode(event.target.value)
    setError('')
  }, [])

  const handleCountryChange = useCallback((event) => {
    const value = event.target.value
    const nextCountryIndex = value === '' ? -1 : Number(value)

    setCountryIndex(nextCountryIndex)
    setStateIndex(-1)
    setError('')
    setErrorFields((prev) => prev.filter((field) => field !== 'country' && field !== 'state'))
  }, [])

  const handleStateChange = useCallback((event) => {
    const value = event.target.value
    const nextStateIndex = value === '' ? -1 : Number(value)

    setStateIndex(nextStateIndex)
    setError('')
    setErrorFields((prev) => prev.filter((field) => field !== 'state'))
  }, [])

  const getData = useCallback(() => {
    const selectedCountry = countryIndex > -1 ? countries[countryIndex] : null
    const selectedState = selectedCountry && stateIndex > -1
      ? selectedCountry.states[stateIndex]
      : ''

    return {
      address: streetAddress,
      city,
      zip_code: zipCode,
      country: selectedCountry ? selectedCountry.country : '',
      state: selectedState || '',
    }
  }, [streetAddress, city, zipCode, countryIndex, stateIndex])

  const validate = useCallback(() => {
    const newErrorFields = []

    if (countryIndex < 0) {
      newErrorFields.push('country')
    }

    if (stateIndex < 0) {
      newErrorFields.push('state')
    }

    if (newErrorFields.length > 0) {
      setErrorFields(newErrorFields)

      if (newErrorFields.includes('country')) {
        return formatMessage({
          id: 'auth.address.selectCountry',
          defaultMessage: 'Please select a country',
          description: 'Please select a country',
        })
      }

      return formatMessage({
        id: 'auth.address.selectState',
        defaultMessage: 'Please select a state',
        description: 'Please select a state',
      })
    }

    setErrorFields([])
    return ''
  }, [countryIndex, stateIndex, formatMessage])

  const submit = useCallback(() => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      window.scrollTo(0, 0)
      return
    }

    const address = getData()
    setError('')
    onSubmit({ address })
  }, [validate, getData, onSubmit])

  const hasError = useCallback((field) => errorFields.includes(field), [errorFields])

  return (
    <DivAccount>
      <NavigationBar
        logOutOnly
        logOut={logout}
        backFunc={onPrevious}
        name={
          formatMessage({
            id: 'auth.address.title',
            defaultMessage: 'Profile Information',
            description: 'Profile Information',
          })
        }
      />
      <AuthCard>

        <Title>
          <FormattedMessage
            id='auth.address.heading'
            defaultMessage='Address'
            description='Address label'
          />
        </Title>
        <Subtitle>
          <FormattedMessage
            id='auth.address.where'
            defaultMessage='Knowing more about you helps us improve your Altrac experience. Fields marked with a (*) are required.'
            description='Knowing where you are label'
          />
        </Subtitle>

        {
          error && (
            <MessageError>
              <TitleError>
                <FormattedMessage
                  id='auth.address.invalid.label'
                  defaultMessage='INVALID INPUT'
                  description='INVALID INPUT label'
                />
              </TitleError>
              <Message>
                {error}
              </Message>
            </MessageError>
          )
        }

        {
          submitError && (
            <MessageError>
              <TitleError>
                <FormattedMessage
                  id='auth.address.submit.error'
                  defaultMessage='SUBMIT ERROR'
                  description='SUBMIT ERROR label'
                />
              </TitleError>
              <Message>
                {submitError}
              </Message>
            </MessageError>
          )
        }

        <Label>
          <FormattedMessage
            id='auth.address.streetAddress'
            defaultMessage='Street Address'
            description='Street Address label'
          />
          <StyledInput
            value={streetAddress}
            onChange={handleAddressChange}
            name='ship-address'
            autoComplete='shipping street-address'
            autoFocus
          />
        </Label>

        <Label>
          <FormattedMessage
            id='auth.address.city'
            defaultMessage='City'
            description='City label'
          />
          <StyledInput
            value={city}
            onChange={handleCityChange}
            name='ship-city'
            autoComplete='shipping locality'
          />
        </Label>

        <CountryStateCombo
          countryValue={countryIndex}
          stateValue={stateIndex}
          hasError={hasError}
          onCountryChange={handleCountryChange}
          onStateChange={handleStateChange}
        />

        <Label>
          <FormattedMessage
            id='auth.address.zip'
            defaultMessage='Zip Code'
            description='Zip Code label'
          />
          <StyledInput
            value={zipCode}
            onChange={handleZipCodeChange}
            name='ship-zip'
            autoComplete='shipping postal-code'
          />
        </Label>

        <ButtonWhite onClick={submit}>
          <FormattedMessage
            id='setting.save'
            defaultMessage='SAVE'
            description='SAVE label'
          />
        </ButtonWhite>
      </AuthCard>
      <Footer />
    </DivAccount>
  )
}

export default injectIntl(Address)
